/**
 * Reporting timezone presentation helpers for the super-admin dashboard.
 *
 * Operators read "Pacific Time", not "America/Los_Angeles", so the scope line and the
 * viewer-mismatch notice render the resolved reporting zone through these helpers.
 * Every function tolerates an empty or invalid IANA name: `Intl` throws a RangeError on an
 * any zone, and these are called from template bindings that re-run on change detection.
 */

type ZoneNameStyle = 'short' | 'long';

/**
 * Formatter cache keyed by style + zone. Callers bind these helpers in templates, so a new
 * `Intl.DateTimeFormat` per change-detection pass would be wasteful; `null` caches a zone that
 * `Intl` rejected so an invalid name is only probed once.
 */
const zoneNameFormatters = new Map<string, Intl.DateTimeFormat | null>();
const zoneTimeFormatters = new Map<string, Intl.DateTimeFormat | null>();
const zoneIsoDayFormatters = new Map<string, Intl.DateTimeFormat | null>();

/** Renders an already-resolved calendar day; the day carries no zone of its own by then. */
const utcDayFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
});

/**
 * Shape of a real IANA zone name: ASCII letters, digits and `_ + - .` in slash-separated segments,
 * e.g. 'UTC', 'America/Los_Angeles', 'America/Argentina/Buenos_Aires', 'Etc/GMT+5'.
 */
const IANA_ZONE_PATTERN = /^[A-Za-z0-9_+.-]+(?:\/[A-Za-z0-9_+.-]+)*$/;

/** No real zone name comes close; anything longer is corrupt input, not a zone. */
const MAX_ZONE_NAME_LENGTH = 64;

/**
 * The one gate every zone argument passes through.
 *
 * `resolved_timezone` is free text on the `locations` row, so a value reaching these helpers may be
 * blank, a non-string from an untyped caller, or something with quotes and angle brackets in it.
 * Screening the shape here keeps such values away from `Intl` and out of the formatter caches, so a
 * hostile or corrupt name degrades to an empty label instead of interning a cache entry. A
 * well-formed but nonexistent zone ('Foo/Bar') still passes this gate and is rejected by `Intl`.
 * @param ianaZone Candidate zone name from any source.
 * @returns The trimmed name, or '' when it is blank, not a string, or not shaped like a zone.
 */
function normalizeZone(ianaZone: string | null | undefined): string {
  if (typeof ianaZone !== 'string') {
    return '';
  }
  const zone = ianaZone.trim();
  if (!zone || zone.length > MAX_ZONE_NAME_LENGTH || !IANA_ZONE_PATTERN.test(zone)) {
    return '';
  }
  return zone;
}

/**
 * Whether `at` is a real Date carrying a usable instant.
 *
 * The `Date` annotation on these helpers is erased at runtime and template bindings re-run on every
 * change-detection pass, so a component field that is still null or a raw ISO string arrives here
 * as-is; calling `.getTime()` on it would throw a TypeError and blank the page.
 * @param at Candidate instant from any source.
 * @returns True only for a Date that is not Invalid Date.
 */
function isUsableInstant(at: any): at is Date {
  return at instanceof Date && !Number.isNaN(at.getTime());
}

/**
 * Cached formatter for a zone, or null when the zone is blank or not a valid IANA name.
 */
function getCachedFormatter(
  cache: Map<string, Intl.DateTimeFormat | null>,
  key: string,
  create: () => Intl.DateTimeFormat
): Intl.DateTimeFormat | null {
  if (cache.has(key)) {
    return cache.get(key) ?? null;
  }
  let formatter: Intl.DateTimeFormat | null = null;
  try {
    formatter = create();
  } catch {
    formatter = null;
  }
  cache.set(key, formatter);
  return formatter;
}

/** Formatter that renders only the timezone name for `ianaZone`, or null when unusable. */
function getZoneNameFormatter(zone: string, style: ZoneNameStyle): Intl.DateTimeFormat | null {
  if (!zone) {
    return null;
  }
  return getCachedFormatter(
    zoneNameFormatters,
    `${style}:${zone}`,
    () => new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: style })
  );
}

/** The timeZoneName part of a formatted instant, or '' when the zone is unusable. */
function extractZoneName(zone: string, style: ZoneNameStyle, at: Date): string {
  const formatter = getZoneNameFormatter(zone, style);
  if (!formatter || !isUsableInstant(at)) {
    return '';
  }
  try {
    const parts = formatter.formatToParts(at);
    return parts.find(part => part.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/**
 * DST-aware short abbreviation for a reporting zone, e.g. 'PDT' in July and 'PST' in January.
 * The abbreviation is always derived from the instant, never hardcoded per zone.
 * @param ianaZone IANA zone name, e.g. 'America/Los_Angeles'.
 * @param at Instant to resolve the abbreviation at; defaults to now.
 * @returns The abbreviation, or '' when the zone is empty or invalid.
 */
export function formatZoneShort(
  ianaZone: string | null | undefined,
  at: Date = new Date()
): string {
  return extractZoneName(normalizeZone(ianaZone), 'short', at);
}

/**
 * Season-neutral long name for a reporting zone, e.g. 'Pacific Time' in both July and January.
 * The Standard/Daylight wording is normalised out so a scope line reads the same year round.
 * @param ianaZone IANA zone name, e.g. 'America/Los_Angeles'.
 * @param at Instant to resolve the name at; defaults to now.
 * @returns The long name, or '' when the zone is empty or invalid.
 */
export function formatZoneLong(ianaZone: string | null | undefined, at: Date = new Date()): string {
  const raw = extractZoneName(normalizeZone(ianaZone), 'long', at);
  if (!raw) {
    return '';
  }
  return raw
    .replace(/\b(Standard|Daylight|Summer|Winter)\s+/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * The viewer's own zone as reported by the browser, or '' when unavailable.
 *
 * Exported because the mismatch notice has to name this zone with the same source
 * `isViewerZoneDifferent` compared against. Naming it from stored profile or shop state instead
 * lets the notice contradict its own trigger — or print an empty slot on a session that has none.
 * @returns The browser's IANA zone name, e.g. 'America/New_York', or '' when unavailable.
 */
export function getViewerZone(): string {
  try {
    return normalizeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    return '';
  }
}

/**
 * Whether the viewer is browsing from a different zone than the shop reports in, which is when
 * the dashboard shows the "figures are in the shop's time" notice.
 * @param shopZone The shop's reporting zone.
 * @returns False when the shop zone is empty, invalid, or the same name as the viewer's zone.
 */
export function isViewerZoneDifferent(shopZone: string | null | undefined): boolean {
  const zone = normalizeZone(shopZone);
  if (!zone || !getZoneNameFormatter(zone, 'short')) {
    return false;
  }
  const viewerZone = getViewerZone();
  if (!viewerZone) {
    return false;
  }
  return viewerZone.toLowerCase() !== zone.toLowerCase();
}

/**
 * The shop's current wall clock, for the viewer-mismatch notice.
 * @param ianaZone The shop's reporting zone.
 * @param at Instant to render; defaults to now.
 * @returns A time like '2:15 PM', or '' when the zone is empty or invalid.
 */
export function nowInZone(ianaZone: string | null | undefined, at: Date = new Date()): string {
  const zone = normalizeZone(ianaZone);
  if (!zone || !isUsableInstant(at)) {
    return '';
  }
  const formatter = getCachedFormatter(
    zoneTimeFormatters,
    zone,
    () =>
      new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
  );
  if (!formatter) {
    return '';
  }
  try {
    return formatter.format(at);
  } catch {
    return '';
  }
}

/**
 * The calendar day `offsetDays` away from today as that zone reckons it, e.g. 'Aug 13'.
 *
 * The day is resolved in the reporting zone and then stepped by whole days, never by subtracting
 * 24 hours from the instant, so a DST transition cannot move the label off the day the figures
 * were bucketed on. Guarded like every other helper here: `resolved_timezone` is free text from
 * the `locations` table, and an any zone reaching an unguarded `Intl` constructor inside a
 * template binding takes the whole page down rather than degrading one label.
 * @param ianaZone IANA zone name the figures were bucketed in.
 * @param offsetDays Whole days to step from today; 0 is today, -1 yesterday.
 * @param at Instant to read "today" at; defaults to now.
 * @returns The formatted day, or '' when the zone is empty or invalid.
 */
export function calendarDayInZone(
  ianaZone: string | null | undefined,
  offsetDays = 0,
  at: Date = new Date()
): string {
  const zone = normalizeZone(ianaZone);
  if (!zone || !isUsableInstant(at)) {
    return '';
  }
  const formatter = getCachedFormatter(
    zoneIsoDayFormatters,
    zone,
    () =>
      new Intl.DateTimeFormat('en-CA', {
        timeZone: zone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
  );
  if (!formatter) {
    return '';
  }
  try {
    const day = new Date(`${formatter.format(at)}T00:00:00Z`);
    if (Number.isNaN(day.getTime())) {
      return '';
    }
    day.setUTCDate(day.getUTCDate() + offsetDays);
    return utcDayFormatter.format(day);
  } catch {
    return '';
  }
}
